import React from 'react';
import { Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import Button from '../shared/Button/Button';
import Input from '../shared/Input/Input';
import SearchableSelect from '../shared/SearchableSelect/SearchableSelect';
import type { Stop, RouteStop } from '../../types/api';

interface Props {
  allStops: Stop[];
  stops: RouteStop[];
  onChange: (stops: RouteStop[]) => void;
}

const RouteStopsBuilder: React.FC<Props> = ({ allStops, stops, onChange }) => {
  const usedIds = new Set(stops.map(s => s.stop_id));
  const available = allStops.filter(s => s.is_active && !usedIds.has(s.id));

  const setMinutes = (idx: number, minutes: number) => {
    const next = stops.map((s, i) => i === idx ? { ...s, minutes_from_origin: minutes } : s);
    onChange(next);
  };

  const remove = (idx: number) => {
    const next = stops.filter((_, i) => i !== idx).map((s, i) => ({ ...s, sequence: i + 1 }));
    onChange(next);
  };

  const move = (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= stops.length) return;
    const next = [...stops];
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next.map((s, i) => ({ ...s, sequence: i + 1 })));
  };

  const addStop = (stop: Stop) => {
    onChange([...stops, {
      stop_id: stop.id, name: stop.name,
      sequence: stops.length + 1, minutes_from_origin: 0,
    }]);
  };

  return (
    <div className="space-y-3">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
          <tr>
            <th className="px-3 py-2">#</th>
            <th className="px-3 py-2">Stop</th>
            <th className="px-3 py-2">Minutes from origin</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {stops.map((s, i) => (
            <tr key={s.stop_id} className="border-b border-slate-100">
              <td className="px-3 py-2">{s.sequence}</td>
              <td className="px-3 py-2">{s.name}</td>
              <td className="px-3 py-2">
                <Input
                  type="number"
                  value={String(s.minutes_from_origin)}
                  onChange={(e) => setMinutes(i, parseInt(e.target.value || '0', 10))}
                  disabled={i === 0}
                />
              </td>
              <td className="px-3 py-2 text-right">
                <div className="inline-flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => move(i, 1)} disabled={i === stops.length - 1}><ArrowDown className="h-4 w-4" /></Button>
                  <Button variant="danger" size="sm" onClick={() => remove(i)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {available.length > 0 && (
        <div className="flex items-center gap-2">
          <div className="w-72">
            <SearchableSelect
              aria-label="Add stop"
              options={available.map((s) => ({ value: String(s.id), label: s.name }))}
              value=""
              onChange={(v) => {
                const id = parseInt(v, 10);
                const stop = available.find((s) => s.id === id);
                if (stop) addStop(stop);
              }}
              placeholder="Add stop…"
              searchPlaceholder="Search stops…"
            />
          </div>
          <span className="text-xs text-slate-500">First stop is the origin; the last is the destination.</span>
        </div>
      )}
    </div>
  );
};

export default RouteStopsBuilder;
